import { useConnections } from '@integration-app/react';
import { ChatCircle, Envelope, Phone } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Label } from 'src/components/Label';
import { TagMultiSelect } from 'src/components/TagMultiSelect';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Number } from 'src/interfaces/number.interface';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { processPhoneNumber } from 'src/utils/number';
import { useWizard } from '../../../components/wizard/WizardContext';
import { EmailProviderSelect } from './EmailProviderSelect';
import { SmsProviderSelect } from './SmsProviderSelect';
interface OutboundConfigurationsProps {
  className?: string;
}

export const OutboundConfigurations = ({
  className = '',
}: OutboundConfigurationsProps) => {
  const { campaignAutomation, setCampaignAutomation } = useWizard();

  const { get } = useAuthedRequest();
  const { connections: integrationAppConnections } = useConnections();

  const [numbers, setNumbers] = useState<Number[]>([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>(
    campaignAutomation.fromNumberOptions.map((o) => String(o.value)),
  );
  const [useSmartMatching, setUseSmartMatching] = useState(true);

  const fetchNumbers = async () => {
    setLoadingNumbers(true);
    const data = await get(`/numbers`);
    setNumbers(data.items);
    setLoadingNumbers(false);
  };

  useEffect(() => {
    fetchNumbers();
  }, []);

  const hasOutreachByType = (type: CommunicationChannel) => {
    return (
      campaignAutomation.followUpCommunications.length > 0 &&
      campaignAutomation.followUpCommunications.some(
        (c) => c.contactVia === type,
      )
    );
  };

  const isValidEmailConfig = useMemo(() => {
    const emailFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.EMAIL,
    );

    if (emailFollowUps.length === 0) return true;

    // Check if campaign-level email provider is set and authenticated
    if (!campaignAutomation.emailProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key ===
          campaignAutomation.emailProviderOption?.value && !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.emailProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  const isValidSmsConfig = useMemo(() => {
    const smsFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.SMS,
    );

    if (smsFollowUps.length === 0) return true;

    // Check if campaign-level SMS provider is set and authenticated
    if (!campaignAutomation.smsProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.smsProviderOption?.value &&
        !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.smsProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Outbound Configurations
        </h2>
        <p className="text-neutral-600">
          Set up the numbers and addresses your campaign will use to reach
          contacts
        </p>
      </div>

      {/* Phone Calls Section */}
      <Card className="p-6 bg-neutral-50 border-neutral-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Phone size={20} className="text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            For phone calls
          </h3>
        </div>

        <div className="space-y-4">
          {/* Select from numbers */}
          <TagMultiSelect
            disabled={loadingNumbers}
            label="Select from numbers"
            values={selectedNumbers}
            onChange={(numbers) => {
              setSelectedNumbers(numbers);
              setCampaignAutomation({
                ...campaignAutomation,
                fromNumberOptions: numbers.map((n) => ({ value: n, label: n })),
              });
            }}
            options={numbers.map((n) => ({
              value: n.number,
              label: processPhoneNumber(n.number),
            }))}
            placeholder="Select phone number..."
            tagColor="primary"
            maxVisibleTags={3}
            required
          />

          {/* Smart number matching checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              checked={useSmartMatching}
              onChange={setUseSmartMatching}
              className="mt-1"
              ariaLabel="Use smart number matching"
            />
            <div onClick={() => setUseSmartMatching(!useSmartMatching)}>
              <Label className="text-sm font-medium text-neutral-700">
                Use smart number matching
              </Label>
              <p className="text-sm text-neutral-500 mt-1">
                We'll call using the number from your selections closest to the
                contact's location.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Email Section */}
      {hasOutreachByType(CommunicationChannel.EMAIL) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Envelope size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              For emails
            </h3>
          </div>

          <EmailProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                emailProviderOption: provider,
              });
            }}
            isAuthenticated={isValidEmailConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of emails may result in your domain
            being flagged as spam.
          </p>
        </Card>
      )}

      {/* SMS Messages Section */}
      {hasOutreachByType(CommunicationChannel.SMS) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ChatCircle size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900">
              For SMS Messages
            </h3>
          </div>

          <SmsProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                smsProviderOption: provider,
              });
            }}
            isAuthenticated={isValidSmsConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of SMS messages may result in your
            numbers being flagged as spam.
          </p>
        </Card>
      )}
    </div>
  );
};
